import { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ServerStatus } from './ServerStatus';
import { api } from '../services/api';

const SEVERITY_BADGE = {
  HIGH:    'bg-red-100 text-red-700',
  MEDIUM:  'bg-orange-100 text-orange-700',
  WARNING: 'bg-yellow-100 text-yellow-700',
  INFO:    'bg-blue-100 text-blue-700',
};

const CATEGORY_COLOR = {
  ALARM:         'bg-red-400',
  VIDEO_ALARM:   'bg-pink-400',
  SYSTEM:        'bg-purple-400',
  POWER:         'bg-yellow-400',
  COMMUNICATION: 'bg-indigo-400',
  DEVICE:        'bg-green-400',
  ACCESS:        'bg-cyan-400',
  MAINTENANCE:   'bg-blue-400',
  UNKNOWN:       'bg-gray-400',
};

const STATUS_DOT = {
  CONFIRMED: 'text-red-500',
  FALSE:     'text-green-500',
  PENDING:   'text-amber-400',
};

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts);
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatDuration(ms) {
  if (!ms) return '—';
  const m = Math.floor(ms / 60000);
  if (m < 1)  return '<1m';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export const Dashboard = ({ events }) => {
  const [loginStats, setLoginStats] = useState({ todayLogins: 0 });
  const [dashboardStats, setDashboardStats] = useState({ todayCount: null, byHour: [] });

  useEffect(() => {
    api.fetchLoginStats().then(setLoginStats).catch(() => {});
  }, []);

  useEffect(() => {
    api.fetchDashboardStats().then(setDashboardStats).catch(() => {});
  }, [events]); // re-fetch whenever events refresh

  // ── KPI numbers ──────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const total        = events.length;
    const todayEvents  = events.filter(e => new Date(e.timestamp) >= todayStart).length;
    const acknowledged = events.filter(e => e.acknowledged).length;
    const confirmed    = events.filter(e => e.alarmStatus === 'CONFIRMED').length;
    const falseAlarms  = events.filter(e => e.alarmStatus === 'FALSE').length;
    const pending      = events.filter(e => e.alarmStatus === 'PENDING').length;
    const uniquePanels = new Set(
      events.filter(e => e.location?.fullCode).map(e => e.location.fullCode)
    ).size;

    const ackRate = total > 0 ? Math.round((acknowledged / total) * 100) : 0;

    const ackTimes = events
      .filter(e => e.acknowledged && e.acknowledgedAt && e.timestamp)
      .map(e => new Date(e.acknowledgedAt) - new Date(e.timestamp));
    const avgAckMs = ackTimes.length
      ? ackTimes.reduce((a, b) => a + b, 0) / ackTimes.length
      : 0;

    return { total, todayEvents, acknowledged, confirmed, falseAlarms, pending, uniquePanels, ackRate, avgAckMs };
  }, [events]);

  // ── 24-hour bar chart (uses aggregate data from server) ───────────────────
  const chartData = useMemo(() => {
    const hourMap = {};
    dashboardStats.byHour.forEach(({ _id, count }) => { hourMap[_id] = count; });
    const now = new Date();
    return Array.from({ length: 24 }, (_, i) => {
      const h = new Date(now);
      h.setHours(now.getHours() - (23 - i), 0, 0, 0);
      const key = h.toLocaleTimeString('en-ZA', { hour: '2-digit', hour12: false }).padStart(2, '0');
      return {
        label: key,
        events: hourMap[key] ?? 0,
      };
    });
  }, [dashboardStats.byHour]);

  // ── Top 5 categories ─────────────────────────────────────────────────────
  const categoryData = useMemo(() => {
    const counts = {};
    events.forEach(e => {
      const cat = e.category || 'UNKNOWN';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({
        name,
        count,
        pct: events.length > 0 ? Math.round((count / events.length) * 100) : 0,
      }));
  }, [events]);

  // ── Recent activity feed ──────────────────────────────────────────────────
  const recentEvents = useMemo(
    () => [...events].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 7),
    [events]
  );

  // ── Coverage counts ───────────────────────────────────────────────────────
  const coverage = useMemo(() => ({
    clients:   new Set(events.filter(e => e.location?.clientCode).map(e => e.location.clientCode)).size,
    locations: new Set(events.filter(e => e.location?.city).map(e => e.location.city)).size,
    sites:     new Set(events.filter(e => e.location?.siteCode && e.location?.clientCode)
                  .map(e => `${e.location.clientCode}-${e.location.siteCode}`)).size,
    panels:    dashboardStats.totalPanels ?? stats.uniquePanels,
  }), [events, stats.uniquePanels, dashboardStats.totalPanels]);

  return (
    <div className="py-20 px-6 max-w-7xl mx-auto space-y-6">

      {/* ── Header row ── */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Overview</h1>
        <ServerStatus />
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Today's Events</div>
          <div className="text-3xl font-bold text-gray-900">
            {dashboardStats.todayCount ?? stats.todayEvents}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Ack Rate</div>
          <div className="text-3xl font-bold text-gray-900">{stats.ackRate}%</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Active Panels</div>
          <div className="text-3xl font-bold text-gray-900">{stats.uniquePanels}</div>
          <div className="text-xs text-gray-400 mt-1">generating events</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-amber-400">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pending Review</div>
          <div className="text-3xl font-bold text-amber-600">{stats.pending}</div>
          <div className="text-xs text-gray-400 mt-1">awaiting classification</div>
        </div>
      </div>

      {/* ── Chart + Feed ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* 24-hour bar chart */}
        <div className="lg:col-span-3 bg-white rounded-lg shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">24-Hour Activity</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={3} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: '#f3f4f6' }}
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  formatter={v => [v, 'Events']}
                />
                <Bar dataKey="events" fill="#6366f1" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent activity feed */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Recent Activity</h3>
          <div className="space-y-0">
            {recentEvents.map(e => (
              <div key={e._id} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                <span className={`mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${SEVERITY_BADGE[e.severity] || 'bg-gray-100 text-gray-600'}`}>
                  {e.severity || '—'}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-800 truncate">{e.description || e.category}</div>
                  <div className="text-xs text-gray-400">
                    {e.location?.city || e.accountNumber} · {timeAgo(e.timestamp)}
                  </div>
                </div>
                {e.alarmStatus && (
                  <span className={`text-xs shrink-0 mt-1 ${STATUS_DOT[e.alarmStatus] || 'text-gray-300'}`}>●</span>
                )}
              </div>
            ))}
            {recentEvents.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-8">No events in selected period</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Alarm types + Ack overview ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Category breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Alarm Types</h3>
          <div className="space-y-3">
            {categoryData.map(({ name, count, pct }) => (
              <div key={name}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{name}</span>
                  <span className="text-gray-500">
                    {count} <span className="text-gray-400 text-xs">({pct}%)</span>
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${CATEGORY_COLOR[name] || 'bg-gray-400'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            ))}
            {categoryData.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-4">No data</div>
            )}
          </div>
        </div>

        {/* Acknowledgement overview */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Acknowledgement Overview</h3>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-700">{stats.confirmed}</div>
              <div className="text-xs text-red-600 mt-1">Confirmed</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">{stats.falseAlarms}</div>
              <div className="text-xs text-green-600 mt-1">False Alarm</div>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-700">{stats.pending}</div>
              <div className="text-xs text-amber-600 mt-1">Pending</div>
            </div>
          </div>

          {/* Stacked bar */}
          {stats.total > 0 && (
            <div className="flex h-2.5 rounded-full overflow-hidden mb-5">
              <div className="bg-red-400 transition-all" style={{ width: `${(stats.confirmed / stats.total) * 100}%` }} />
              <div className="bg-green-400 transition-all" style={{ width: `${(stats.falseAlarms / stats.total) * 100}%` }} />
              <div className="bg-amber-300 transition-all" style={{ width: `${(stats.pending / stats.total) * 100}%` }} />
              <div className="bg-gray-200 flex-1" />
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 pt-2 border-t">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Ack Rate</div>
              <div className="text-xl font-bold text-gray-900 mt-0.5">{stats.ackRate}%</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Avg Response</div>
              <div className="text-xl font-bold text-gray-900 mt-0.5">{formatDuration(stats.avgAckMs)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Today Logins</div>
              <div className="text-xl font-bold text-gray-900 mt-0.5">{loginStats.todayLogins}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Coverage ── */}
      <div className="bg-white rounded-lg shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Coverage</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Clients',   value: coverage.clients,   cls: 'bg-blue-50 text-blue-700' },
            { label: 'Locations', value: coverage.locations, cls: 'bg-purple-50 text-purple-700' },
            { label: 'Sites',     value: coverage.sites,     cls: 'bg-yellow-50 text-yellow-700' },
            { label: 'Panels',    value: coverage.panels,    cls: 'bg-red-50 text-red-700' },
          ].map(({ label, value, cls }) => (
            <div key={label} className={`${cls} rounded-lg p-4 text-center`}>
              <div className="text-2xl font-bold">{value}</div>
              <div className="text-sm mt-1">{label}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};
